import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { ButtonWithIcon } from '@els/els-react--button';
import NumberFormatter from '@Utils/NumberFormatter';
import ResultHitNavigation from '@Components/Results/common/ResultHitNavigation';

import './ResultDetailNav.scss';

function ResultDetailNav({ items, activeId, onToggle, onOpen, onClose, ariaLabel, className }) {
  const visibleItems = (items || []).filter(Boolean);
  if (!visibleItems.length) return null;

  return (
    // RX-23573: not a `nav` landmark. Rendered once per result row, so a landmark here multiplied
    // into dozens of identical "Document data categories" navigation landmarks — landmark overuse
    // (ARIA APG). The category toggles remain reachable in normal reading order.
    <ResultHitNavigation ariaLabel={ariaLabel}>
      <ul className={classnames('result-detail-nav', className)}>
        {visibleItems.map((item) => {
          if (item.node) {
            return (
              <li className="result-detail-nav__item" key={item.id}>
                {item.node}
              </li>
            );
          }

          const isOpen = item.id === activeId;
          const label = item.count ? `${item.title || ''} - ${NumberFormatter(item.count, 0)}` : item.title || '';
          return (
            <li
              className={classnames('result-detail-nav__item', { 'result-detail-nav__item--separated': item.separatorLeft })}
              key={item.id}>
              <ButtonWithIcon
                data-e2e={item.dataE2e}
                data-nav-id={item.id}
                {...item.attrs}
                className="result-detail-nav__button rx-no-wrap u-els-font-size-base"
                type="link"
                size="small"
                iconRight
                linkIconSize="xs"
                sprite={item.sprite || (isOpen ? 'ChevronUp' : 'ChevronDown')}
                aria-expanded={isOpen}
                aria-controls={item.controls}
                onClick={(event) => {
                  event.preventDefault();
                  if (item.id === activeId) {
                    (onClose || onToggle)(item.id);
                  } else {
                    (onOpen || onToggle)(item.id);
                  }
                }}>
                {label}
              </ButtonWithIcon>
            </li>
          );
        })}
      </ul>
    </ResultHitNavigation>
  );
}

ResultDetailNav.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string,
      count: PropTypes.number,
      node: PropTypes.node,
      controls: PropTypes.string,
      dataE2e: PropTypes.string,
      sprite: PropTypes.string,
      separatorLeft: PropTypes.bool,
      attrs: PropTypes.object,
    })
  ).isRequired,
  activeId: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

export default ResultDetailNav;
